import React, { useState, useRef, useEffect } from 'react'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Radio from '~/Common-Objects/UI/Forms/Radio/Radio'
import TextArea from '~/Common-Objects/UI/Forms/TextArea/TextArea'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import axios from '~/data/http/axios'
import i18n from '~/i18n'

export default function NewUserMoreQuestions({ onFinish = () => {} }) {
    const providers = useRef(null)
    const dnsEmail = useRef(null)
    const [answers, setAnswers] = useState({
        name: '',
        address: '',
        companyNo: '',
        age: '',
        usedBefore: undefined,
        usedBeforeProvider: '',
        contactsSource: '',
        listPurchased: undefined,
        domains: '',
        dnsAccess: undefined,
        dnsAccessEmail: '',
        content: ''
    })
    const [newUserAnalysis, setNewUserAnalysis] = useState(null)
    const [showContactsFromQuestion, setShowContactsFromQuestion] = useState(false)
    const [showDomainsQuestion, setShowDomainsQuestion] = useState(false)
    const [processing, setProcessing] = useState(false)

    const finish = async () => {
        setProcessing(true)
        try {
            await axios.post('/accountMaster/morequestions', answers)
        } catch (e) {
            console.log(e)
            setProcessing(false)
            return
        }
        // validation to make sure they answered all the questions
        // if all good then send the answers to the api and call onFinish
        setProcessing(false)
        onFinish(answers)
    }

    useEffect(() => {
        if (answers.usedBefore && providers.current) {
            providers.current.focus()
            providers.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }, [answers.usedBefore])

    useEffect(() => {
        if (answers.dnsAccess === false && dnsEmail.current) {
            dnsEmail.current.focus()
            dnsEmail.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }, [answers.dnsAccess])

    useEffect(() => {
        if (!newUserAnalysis) return
        if (!newUserAnalysis.subscribers || newUserAnalysis.subscribers > 3000) {
            setShowContactsFromQuestion(true)
        }
        if (!newUserAnalysis.domains || newUserAnalysis.domains.length === 0) {
            setShowDomainsQuestion(true)
        } else if (newUserAnalysis.domains.every((d) => d.authenticated === false)) {
            setShowDomainsQuestion(true)
        }
    }, [newUserAnalysis])

    useEffect(() => {
        axios.get('/accountMaster').then((res) => {
            setNewUserAnalysis(res.data.AccountMaster.options?.newUserAnalysis || {})
        })
    }, [])

    if (!newUserAnalysis) return <LoadingIcons />

    return (
        <>
            <div style={{ marginBottom: 40 }}>
                <div
                    style={{
                        fontWeight: 600,
                        fontStyle: 'Semi Bold',
                        fontSize: 20,
                        leadingTrim: 'NONE',
                        lineHeight: '24px',
                        letterSpacing: -0.4,
                        textAlign: 'center',
                        marginBottom: '24px'
                    }}
                >
                    {i18n.t('setup:newUser.title')}
                </div>
                <div
                    style={{
                        fontWeight: 400,
                        fontStyle: 'Regular',
                        fontSize: '14px',
                        leadingTrim: 'NONE',
                        lineHeight: '20px',
                        letterSpacing: '0px',
                        textAlign: 'center'
                    }}
                >
                    {i18n.t('setup:newUser.intro1')}
                    <br />
                    <br />
                    {i18n.t('setup:newUser.intro2')}
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 40 }}>
                <TextInput
                    label={i18n.t('setup:newUser.businessName')}
                    value={answers.name}
                    onChange={(e) => setAnswers({ ...answers, name: e.target.value })}
                />
                <TextArea
                    rows={4}
                    label={i18n.t('setup:newUser.businessAddress')}
                    value={answers.address}
                    onChange={(e) => setAnswers({ ...answers, address: e.target.value })}
                />
                <TextInput
                    label={i18n.t('setup:newUser.companyNo')}
                    value={answers.companyNo}
                    onChange={(e) => setAnswers({ ...answers, companyNo: e.target.value })}
                />
                <TextInput
                    label={i18n.t('setup:newUser.companyAge')}
                    value={answers.age}
                    onChange={(e) => setAnswers({ ...answers, age: e.target.value })}
                />
                <Options
                    label={i18n.t('setup:newUser.usedBefore')}
                    options={[
                        { label: i18n.t('yes'), value: true },
                        { label: i18n.t('no'), value: false }
                    ]}
                    value={answers.usedBefore}
                    onChange={(value) => setAnswers({ ...answers, usedBefore: value })}
                />
                {answers.usedBefore && (
                    <TextInput
                        inputRef={providers}
                        label={i18n.t('setup:newUser.usedBeforeProvider')}
                        value={answers.usedBeforeProvider}
                        onChange={(e) => setAnswers({ ...answers, usedBeforeProvider: e.target.value })}
                    />
                )}

                {showContactsFromQuestion && (
                    <>
                        <TextInput
                            label={i18n.t('setup:newUser.contactsSource')}
                            value={answers.contactsSource}
                            onChange={(e) => setAnswers({ ...answers, contactsSource: e.target.value })}
                        />
                        <Options
                            label={i18n.t('setup:newUser.listPurchased')}
                            options={[
                                { label: i18n.t('yes'), value: true },
                                { label: i18n.t('no'), value: false }
                            ]}
                            value={answers.listPurchased}
                            onChange={(value) => setAnswers({ ...answers, listPurchased: value })}
                        />
                    </>
                )}

                {showDomainsQuestion && (
                    <>
                        <TextArea
                            rows={4}
                            label={i18n.t('setup:newUser.domains')}
                            value={answers.domains}
                            onChange={(e) => setAnswers({ ...answers, domains: e.target.value })}
                        />
                        <Options
                            label={i18n.t('setup:newUser.dnsAccess')}
                            explainer={
                                <div>
                                    {i18n.t('setup:newUser.dnsAccessExplainer')}{' '}
                                    <a
                                        href='https://transpond.io/support/deliverability-and-compliance/authenticating-your-email-domain'
                                        target='_blank'
                                    >
                                        {i18n.t('setup:newUser.learnMore')}
                                    </a>
                                </div>
                            }
                            value={answers.dnsAccess}
                            onChange={(value) => setAnswers({ ...answers, dnsAccess: value })}
                            options={[
                                { label: i18n.t('yes'), value: true },
                                { label: i18n.t('no'), value: false }
                            ]}
                        />
                        {/* {answers.dnsAccess === false && (
                            <TextInput
                                inputRef={dnsEmail}
                                label='Forward domain authentication instructions to a colleague (Optional)'
                                value={answers.dnsAccessEmail}
                                type='email'
                                onChange={(e) => setAnswers({ ...answers, dnsAccessEmail: e.target.value })}
                            />
                        )} */}
                    </>
                )}

                <div style={{ display: 'block', marginTop: 20 }}>
                    <Button
                        disabled={processing}
                        iconRight={<Icons icon='arrowRight' />}
                        onClick={finish}
                        className='button-primary'
                    >
                        {i18n.t('setup:newUser.submit')}
                    </Button>
                </div>
            </div>
        </>
    )
}

function Options({ label, explainer, options, value, onChange }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontWeight: 500, fontSize: 14 }}>{label}</label>
            {!!explainer && (
                <div
                    style={{
                        color: 'var(--neutral-800)',
                        fontWeight: 400,
                        fontStyle: 'Regular',
                        fontSize: 12,
                        leadingTrim: 'none',
                        lineHeight: '16px',
                        letterSpacing: 0,
                        marginBottom: 4
                    }}
                >
                    {explainer}
                </div>
            )}
            {options.map((opt) => (
                <Radio key={opt.value} checked={value === opt.value} onChange={() => onChange(opt.value)}>
                    {opt.label}
                </Radio>
            ))}
        </div>
    )
}
